<template>
  <!-- Fullscreen mobile: ion-modal for iOS card presentation -->
  <ion-modal
    v-if="useIonicModal"
    :is-open="modelValue"
    :class="modalClass"
    :presenting-element="presentingElement"
    @did-dismiss="close">
    <div
      ref="sheetRef"
      class="flex flex-col h-full bg-gray-900 rounded-t-2xl pb-[env(safe-area-inset-bottom,0px)]"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd">
      <!-- Drag handle -->
      <div class="flex justify-center pt-3 pb-1">
        <div class="w-10 h-1 rounded-full bg-white/30" />
      </div>

      <!-- Header -->
      <div class="flex justify-between items-center px-4 pt-1 pb-2">
        <div class="flex-1 min-w-0">
          <slot name="header">
            <span
              v-if="title"
              class="text-sm font-medium truncate text-white/60">
              {{ title }}
            </span>
          </slot>
        </div>
        <button
          type="button"
          class="flex justify-center items-center rounded-full transition-all duration-150 cursor-pointer size-8 bg-white/10 text-white/80 hover:bg-white/20 active:scale-95"
          @click="close">
          <fa-icon :icon="['fas', 'xmark']" />
        </button>
      </div>

      <!-- Content -->
      <div class="px-6 pt-2 pb-6">
        <slot />
      </div>
    </div>
  </ion-modal>

  <!-- All other cases: Teleport overlay with proper backdrop -->
  <Teleport v-else to="body">
    <Transition :name="isMobileApp ? 'sheet-mobile' : 'sheet-desktop'">
      <div
        v-if="modelValue"
        class="sheet-overlay"
        :class="isMobileApp ? 'items-end' : 'items-center'"
        :style="overlayStyle"
        @click.self="close">
        <div
          ref="sheetRef"
          :class="[
            isMobileApp
              ? 'w-full flex flex-col bg-gray-900 rounded-t-2xl pb-[env(safe-area-inset-bottom,0px)] shadow-[0_-10px_40px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)] sheet-container-mobile'
              : 'w-full mx-4 flex flex-col overflow-hidden bg-gray-900 rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)] sheet-container-desktop',
            fullscreen ? 'max-w-[900px] max-h-[90vh]' : 'max-w-md max-h-[85vh]'
          ]">
            <!-- Mobile: Drag handle + header -->
            <template v-if="isMobileApp">
              <div
                class="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
                @touchstart="onTouchStart"
                @touchmove="onTouchMove"
                @touchend="onTouchEnd">
                <div class="w-10 h-1 rounded-full bg-white/30" />
              </div>
              <div
                class="flex justify-between items-center px-4 pt-1 pb-2"
                @touchstart="onTouchStart"
                @touchmove="onTouchMove"
                @touchend="onTouchEnd">
                <div class="flex-1 min-w-0">
                  <slot name="header">
                    <span
                      v-if="title"
                      class="text-sm font-medium truncate text-white/60">
                      {{ title }}
                    </span>
                  </slot>
                </div>
                <button
                  type="button"
                  class="flex justify-center items-center rounded-full transition-all duration-150 cursor-pointer size-8 bg-white/10 text-white/80 hover:bg-white/20 active:scale-95"
                  @click="close">
                  <fa-icon :icon="['fas', 'xmark']" />
                </button>
              </div>
            </template>

            <!-- Desktop: Header -->
            <template v-else>
              <div class="flex justify-between items-center px-4 pt-4 pb-2">
                <div class="flex-1 min-w-0">
                  <slot name="header">
                    <span
                      v-if="title"
                      class="text-sm font-medium truncate text-white/60">
                      {{ title }}
                    </span>
                  </slot>
                </div>
                <button
                  type="button"
                  class="flex justify-center items-center rounded-full transition-all duration-150 cursor-pointer size-8 bg-white/10 text-white/80 hover:bg-white/20 active:scale-95"
                  @click="close">
                  <fa-icon :icon="['fas', 'xmark']" />
                </button>
              </div>
            </template>

            <!-- Content -->
            <div class="px-6 pt-2 pb-6 overflow-y-auto">
              <slot />
            </div>
          </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { inject, ref, watch, onUnmounted, computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  fullscreen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const { isMobileApp, isIOS } = inject('platform', {
  isMobileApp: false,
  isIOS: ref(false)
})

// Only use ion-modal for fullscreen mobile (iOS card presentation)
const useIonicModal = computed(() => {
  const isMobile = typeof isMobileApp === 'object' && 'value' in isMobileApp
    ? isMobileApp.value
    : isMobileApp
  return isMobile && props.fullscreen
})

// Presenting element for iOS card-style fullscreen modals
const presentingElement = ref(null)

// Check if we're inside a card stack context (for z-index layering)
const inCardStackContext = inject('inCardStackContext', false)

// Compute modal class for ion-modal (fullscreen only)
const modalClass = computed(() => {
  const classes = ['options-sheet-modal-fullscreen']
  if (inCardStackContext) {
    classes.push('spectra-modal-card-context')
  }
  const isMobile = typeof isMobileApp === 'object' && 'value' in isMobileApp
    ? isMobileApp.value
    : isMobileApp
  if (isMobile) {
    classes.push('options-sheet-modal-fullscreen-mobile')
  }
  return classes.join(' ')
})

// Set up presenting element for iOS card-style presentation when fullscreen
watch(
  () => props.modelValue,
  async (isOpen) => {
    if (isOpen && useIonicModal.value) {
      const isIOSVal =
        typeof isIOS === 'object' && 'value' in isIOS ? isIOS.value : isIOS
      if (isIOSVal) {
        presentingElement.value = document.querySelector('ion-router-outlet')
      }
    } else if (!isOpen && presentingElement.value) {
      setTimeout(() => {
        presentingElement.value = null
      }, 350)
    }
  }
)

// Overlay z-index style (higher when in card stack context)
const overlayStyle = computed(() => {
  return { zIndex: inCardStackContext ? 60000 : 100 }
})

const close = () => {
  emit('update:modelValue', false)
  emit('close')
}

// ESC key handling
const handleKeydown = (e) => {
  if (e.key === 'Escape' && props.modelValue) {
    close()
  }
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Swipe-to-dismiss handling
const sheetRef = ref(null)
const touchStartY = ref(0)
const currentTranslateY = ref(0)
const isDragging = ref(false)

const onTouchStart = (e) => {
  touchStartY.value = e.touches[0].clientY
  currentTranslateY.value = 0
  isDragging.value = true
  if (sheetRef.value) {
    sheetRef.value.style.transition = 'none'
  }
}

const onTouchMove = (e) => {
  if (!isDragging.value) return

  const deltaY = e.touches[0].clientY - touchStartY.value

  if (deltaY > 0) {
    currentTranslateY.value = deltaY
    if (sheetRef.value) {
      sheetRef.value.style.transform = `translateY(${deltaY}px)`
    }
  }
}

const onTouchEnd = () => {
  isDragging.value = false

  if (sheetRef.value) {
    sheetRef.value.style.transition = 'transform 200ms ease-out'

    const threshold = Math.min(100, sheetRef.value.offsetHeight * 0.3)

    if (currentTranslateY.value > threshold) {
      sheetRef.value.style.transform = `translateY(100%)`
      setTimeout(close, 200)
    } else {
      sheetRef.value.style.transform = 'translateY(0)'
    }
  }

  currentTranslateY.value = 0
}
</script>

<style>
/* Fullscreen options sheet modal - card presentation with push-in effect */
.options-sheet-modal-fullscreen {
  --width: 100%;
  --max-width: 100%;
  --height: 100%;
  --min-height: 100%;
  --max-height: 100%;
  --background: transparent;
  --border-radius: 0;
  --box-shadow: none;
}

.options-sheet-modal-fullscreen::part(content) {
  background: transparent;
}

.options-sheet-modal-fullscreen::part(backdrop) {
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* iOS card presentation - gap at top shows pushed-back content */
.options-sheet-modal-fullscreen.options-sheet-modal-fullscreen-mobile {
  --border-radius: 12px 12px 0 0;
  --height: calc(100% - 44px - env(safe-area-inset-top, 0px));
  --min-height: unset;
  --max-height: calc(100% - 44px - env(safe-area-inset-top, 0px));
}
</style>
